import { create } from 'zustand';

import { Product } from '@lib/product/definitions';
import { Cart } from '@lib/cart/definitions';

/**
 * Manages the shopping cart state and actions.
 *
 * The store includes:
 * - `cart`: The current shopping cart.
 * - `setCart`: A function to update the cart state.
 */
interface CartState {
  cart?: Cart;
  setCart: (cart?: Cart) => void;
  products?: Product[];
  setProducts: (products?: Product[]) => void;
}

/**
 * Zustand store for managing the shopping cart.
 *
 * This store persists cart data using `sessionStorage`, ensuring that cart contents remain
 * available during the browsing session, even after page reloads.
 *
 * Features:
 * - Stores the cart state (`cart`).
 * - Allows modifying the cart through `setCart`.
 * - Uses `sessionStorage` for persistence, so data is cleared when the session ends.
 */
const useCartStore = create<CartState>((set) => ({
  setCart: (cart?: Cart) => set({ cart }),
  setProducts: (products?: Product[]) => set({ products }),
}));

export { useCartStore };
